<template>
  <section v-if="userStore.current" class="header-right">
    <a-dropdown :trigger="['hover']" placement="bottom">
      <div class="language-switch">
        <global-outlined class="icon" />
        <span class="current-lang">{{ currentLangLabel }}</span>
      </div>
      <template #overlay>
        <a-menu class="language-menu">
          <a-menu-item v-for="lang in languages" :key="lang.value" @click="handleChange(lang.value)">
            <span :class="{ active: language === lang.value }">
              {{ lang.label }}
            </span>
          </a-menu-item>
        </a-menu>
      </template>
    </a-dropdown>

    <a-dropdown>
      <a-space class="avatar-trigger">
        <a-avatar :size="28" class="user-avatar">
          <template #icon><img :src="headpic" /></template>
        </a-avatar>
        <span class="user-name">{{ userStore.current?.realName }}</span>
      </a-space>
      <template #overlay>
        <a-menu>
          <a-menu-item @click="toWorkplace">
            <template #icon>
              <robot-outlined />
            </template>
            {{ t('header.workplace') }}
          </a-menu-item>
          <a-menu-item @click="toAccount">
            <template #icon>
              <user-outlined />
            </template>
            {{ t('header.account') }}
          </a-menu-item>
          <a-menu-item @click="toMyWorkItem">
            <template #icon>
              <profile-outlined />
            </template>
            CTC需求进度
          </a-menu-item>
          <a-menu-item @click="userStore.logout">
            <template #icon>
              <logout-outlined />
            </template>
            {{ t('header.logout') }}
          </a-menu-item>
        </a-menu>
      </template>
    </a-dropdown>
  </section>
</template>

<script setup lang="ts">
import { useResidentStore, useUserStore } from '@/store';
import { PermRoleKey } from '@/types/permission';
import { factoryList, setFactoryCache } from '@/utils/factory';
import { hasPermR } from '@/utils/permission';
import { GlobalOutlined, LogoutOutlined, ProfileOutlined, RobotOutlined, UserOutlined } from '@ant-design/icons-vue';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import dayjs from 'dayjs';

const resident = useResidentStore();
const { t, locale } = useI18n();
const language = ref(localStorage.getItem('locale') || locale.value);
// 初始化时同步设置 locale
locale.value = language.value;

const languages = [
  {
    value: 'zh-CN',
    label: hasPermR([
      PermRoleKey.Fqc,
      PermRoleKey.Iqc,
      PermRoleKey.Oqc,
      PermRoleKey.Ipqc,
      PermRoleKey.Pt,
      PermRoleKey.Supply,
      PermRoleKey.SupplyMgr,
    ])
      ? '中文(国内数据)'
      : '中文',
  },
  {
    value: 'th-TH',
    label: hasPermR([
      PermRoleKey.Fqc,
      PermRoleKey.Iqc,
      PermRoleKey.Oqc,
      PermRoleKey.Ipqc,
      PermRoleKey.Pt,
      PermRoleKey.Supply,
      PermRoleKey.SupplyMgr,
    ])
      ? 'ไทย(ข้อมูลประเทศไทย)'
      : 'ไทย',
  },
];

const currentLangLabel = computed(() => {
  return languages.find((lang) => lang.value === language.value)?.label;
});

const handleChange = (value: string) => {
  language.value = value;
  locale.value = value;

  // 判断用户是否属于（外部）组织
  const isOrgUser = userStore.isOrganizationUser();

  // 同时修改 ctcFactory 的 language
  if (value === 'zh-CN') {
    // 保存语言设置到 localStorage
    localStorage.setItem('locale', 'zh-CN');
    // 同步 dayjs 的语言设置
    dayjs.locale('zh');

    // 如果用户配有配置 orgId，才执行原逻辑（ 切换到国内工厂）
    if (!isOrgUser) {
      setFactoryCache(factoryList[0]);
      resident.currentFactory = factoryList[0];
    }
  } else {
    localStorage.setItem('locale', 'th-TH');
    dayjs.locale('en');

    // 如果用户配有配置 orgId，才执行原逻辑（ 切换到泰国工厂）
    if (!isOrgUser) {
      setFactoryCache(factoryList[1]);
      resident.currentFactory = factoryList[1];
    }
  }
};

const router = useRouter();
const userStore = useUserStore();
const headpic = computed(
  () => userStore.current?.avatar || 'https://res-cn.makeblock.com/passport/official/avatar/1.png',
);

const toWorkplace = () => {
  router.push({ name: 'workplace' });
};

const toAccount = () => {
  router.push({ name: 'account' });
};

const toMyWorkItem = () => {
  router.push({ name: 'my-work-item' });
};
</script>

<style scoped>
.header-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.language-switch {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  white-space: nowrap;
  padding: 4px 4px;
  border-radius: 6px;
  transition: all 0.2s;
  color: #86909c;
  font-size: 13px;
}

.language-switch:hover {
  color: #fff;
}

.current-lang {
  position: relative;
  padding: 2px 0;
}

.current-lang::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 1px;
  background-color: #fff;
  transform: scaleX(0);
  transition: transform 0.2s;
  transform-origin: center;
}

.language-switch:hover .current-lang::after {
  transform: scaleX(1);
}

.icon {
  font-size: 14px;
}

.arrow {
  font-size: 12px;
  opacity: 0.8;
}

.language-menu {
  min-width: 100px;
}

.language-menu :deep(.ant-dropdown-menu-item) {
  padding: 8px 16px;
}

.active {
  color: #1664d8;
  font-weight: 500;
}

.avatar-trigger {
  cursor: pointer;
}

.user-avatar {
  background: #3370ff;
}

.user-avatar :deep(img) {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.user-name {
  color: #c9cdd4;
  font-size: 13px;
  user-select: none;
}
</style>
